import { ICrewCustomDataType } from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import _ from "lodash";
import { createRef, PureComponent, ReactNode } from "react";
import { SaveStatus } from "../../../../shared/utility/save-status";
import { KModal } from "../../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../../ui-kit/k-modal/k-modal-footer";
import KuikaButton, { type KuikaButtonStatus } from "../../../ui-kit/kuika-button/kuika-button";
import KuikaCollapsePanel from "../../../ui-kit/kuika-collapse-panel/kuika-collapse-panel";
import KuikaEmptyState from "../../../ui-kit/kuika-empty-state/kuika-empty-state";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import "./ai-agent-modals.scss";

const TYPE_OPTIONS = [
  { value: "str", label: "string" },
  { value: "bool", label: "boolean" },
  { value: "float", label: "float" },
  { value: "integer", label: "integer" },
  { value: "list", label: "list" },
  { value: "object", label: "object" }
];

interface IFormProperty {
  name: string;
  datatype: string;
  description: string;
}

interface IFormCustomDataType {
  type_name: string;
  properties: IFormProperty[];
}

export interface IAiAgentCustomDataTypesModalProps {
  isOpen: boolean;
  customDataTypes: ICrewCustomDataType[];
  onClose: () => void;
  onCustomDataTypesUpdate: (customDataTypes: ICrewCustomDataType[]) => void;
}

export interface IAiAgentCustomDataTypesModalState {
  isLoading?: boolean;
  /**
   * Where Update stands. It writes into the agent being edited, not to the server, so there is no
   * loading phase — only the ✓ held long enough to be read before the dialog leaves.
   */
  applyStatus?: KuikaButtonStatus;
  expandedKeys: string[];
}

export class AiAgentCustomDataTypesModal extends PureComponent<
  IAiAgentCustomDataTypesModalProps,
  IAiAgentCustomDataTypesModalState
> {
  formRef = createRef<FormInstance<any>>();

  private readonly applyStatus: SaveStatus = new SaveStatus((applyStatus: KuikaButtonStatus): void => {
    this.setState({ applyStatus });
  });

  componentWillUnmount(): void {
    this.applyStatus.dispose();
  }

  constructor(props) {
    super(props);
    this.state = {
      expandedKeys: []
    };
  }

  componentDidUpdate(
    prevProps: Readonly<IAiAgentCustomDataTypesModalProps>,
    prevState: Readonly<IAiAgentCustomDataTypesModalState>,
    snapshot?: any
  ): void {
    if (prevProps.isOpen === false && this.props.isOpen === true) {
      this.handleLoad();
    }
  }

  parsePropertyValue = (value: string): { datatype: string; description: string } => {
    // Parse format: "datatype (description)"
    const match = value.match(/^([^(]+)\s*\(([^)]+)\)$/);
    if (match) {
      return {
        datatype: match[1].trim(),
        description: match[2].trim()
      };
    }
    return {
      datatype: value,
      description: ""
    };
  };

  formatPropertyValue = (datatype: string, description: string): string => {
    if (description && description.trim()) {
      return `${datatype} (${description})`;
    }
    return datatype;
  };

  convertToFormData = (customDataTypes: ICrewCustomDataType[]): IFormCustomDataType[] => {
    return customDataTypes.map((dataType) => ({
      type_name: dataType.type_name,
      properties: Object.entries(dataType.properties || {}).map(([name, value]) => {
        const parsed = this.parsePropertyValue(value);
        return {
          name,
          datatype: parsed.datatype,
          description: parsed.description
        };
      })
    }));
  };

  convertFromFormData = (formData: IFormCustomDataType[]): ICrewCustomDataType[] => {
    return formData.map((dataType) => ({
      type_name: dataType.type_name,
      properties: (dataType.properties || []).reduce(
        (acc, prop) => {
          if (prop.name) {
            acc[prop.name] = this.formatPropertyValue(prop.datatype || "str", prop.description || "");
          }
          return acc;
        },
        {} as { [key: string]: string }
      )
    }));
  };

  handleLoad = () => {
    const formData = this.convertToFormData(_.cloneDeep(this.props.customDataTypes || []));
    this.formRef.current?.setFieldsValue({
      customDataTypes: formData
    });
    // Expand all custom types by default
    this.setState({
      expandedKeys: formData.map((_, index) => `customType-${index}`)
    });
  };

  onSubmit = (data: { customDataTypes: IFormCustomDataType[] }) => {
    // A second press during the ✓ would write the same types twice.
    if (!this.applyStatus.canStart) return;
    const customDataTypes = this.convertFromFormData(data.customDataTypes || []);
    // The agent takes the types at once; the ✓ is held on Update, then the dialog leaves.
    this.props.onCustomDataTypesUpdate(customDataTypes);
    this.applyStatus.succeed(this.props.onClose);
  };

  closeModal = (): void => {
    if (this.state.isLoading) return;
    this.props.onClose();
  };

  typeNameValidator = async (index: number, value?: string) => {
    if (!value) throw new Error("Type name is required");
    if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(value)) throw new Error("Please enter a valid type name.");

    const { customDataTypes } = this.formRef.current?.getFieldsValue();

    if (customDataTypes?.find((type, i) => type.type_name === value && i !== index)) {
      throw new Error("This type name is already used.");
    }
  };

  propertyNameValidator = async (typeIndex: number, propIndex: number, value?: string) => {
    if (!value) throw new Error("Property name is required");
    if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(value)) throw new Error("Please enter a valid property name.");

    const { customDataTypes } = this.formRef.current?.getFieldsValue();
    const properties = customDataTypes?.[typeIndex]?.properties || [];

    if (properties.find((prop, i) => prop.name === value && i !== propIndex)) {
      throw new Error("This property name is already used in this type.");
    }
  };

  render(): ReactNode {
    return (
      <>
        <Form ref={this.formRef} onFinish={this.onSubmit}>
          <KModal
            className="kuika-ai-agent-custom-types-modal"
            header="Custom Data Types"
            showModal={this.props.isOpen}
            closeModal={this.closeModal}
            width={800}
            isLoading={this.state.isLoading}
            footer={
              <KModalFooter
                submitLabel="Update"
                status={this.state.applyStatus ?? "idle"}
                statusLabels={{ success: "Updated" }}
                onSubmit={() => {
                  this.formRef.current?.submit();
                }}
              />
            }
          >
            <div className="kuika-ai-agent-custom-types-modal__container">
              <Form.List name="customDataTypes">
                {(typeFields, { add: addType, remove: removeType }) => (
                  <>
                    {typeFields.length > 0 ? (
                      typeFields.map(({ name: typeIndex, key: typeKey, ...typeRestField }) => (
                        <div key={typeKey} className="kuika-ai-agent-custom-types-modal__type-card">
                          <KuikaCollapsePanel
                            animated={true}
                            iconPlacement="right"
                            activeKey={this.state.expandedKeys}
                            onChange={(keys) => {
                              const keysArray = Array.isArray(keys) ? keys : keys ? [keys] : [];
                              this.setState({ expandedKeys: keysArray.map((k) => String(k)) });
                            }}
                            items={[
                              {
                                key: `customType-${typeIndex}`,
                                label: (
                                  <div className="kuika-ai-agent-custom-types-modal__type-header">
                                    <Form.Item
                                      name={[typeIndex, "type_name"]}
                                      rules={[
                                        { validator: (_rule, value) => this.typeNameValidator(typeIndex, value) }
                                      ]}
                                      {...typeRestField}
                                    >
                                      <KuikaInput
                                        placeholder="Type name"
                                        onClick={(e) => e.stopPropagation()}
                                        onKeyDown={(e) => {
                                          if (!e.key.match(/^[a-zA-Z0-9_]*$/)) {
                                            e.preventDefault();
                                          }
                                        }}
                                      />
                                    </Form.Item>
                                    <KuikaButton
                                      type="tertiary"
                                      color="neutral"
                                      isIcon
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        removeType(typeIndex);
                                      }}
                                    >
                                      <KuikaSvg type="common" name="trash" color="var(--red-5)" />
                                    </KuikaButton>
                                  </div>
                                ),
                                children: (
                                  <div className="kuika-ai-agent-custom-types-modal__properties">
                                    <div className="kuika-ai-agent-custom-types-modal__properties-header">
                                      <span className="kuika-ai-agent-custom-types-modal__column-name">Name</span>
                                      <span className="kuika-ai-agent-custom-types-modal__column-type">Type</span>
                                      <span className="kuika-ai-agent-custom-types-modal__column-description">
                                        Description
                                      </span>
                                      <span className="kuika-ai-agent-custom-types-modal__column-actions"></span>
                                    </div>

                                    <Form.List name={[typeIndex, "properties"]}>
                                      {(propFields, { add: addProp, remove: removeProp }) => (
                                        <>
                                          {propFields.map(({ name: propIndex, key: propKey, ...propRestField }) => (
                                            <div
                                              key={propKey}
                                              className="kuika-ai-agent-custom-types-modal__property-row"
                                            >
                                              <div className="kuika-ai-agent-custom-types-modal__column-name">
                                                <Form.Item
                                                  name={[propIndex, "name"]}
                                                  rules={[
                                                    {
                                                      validator: (_rule, value) =>
                                                        this.propertyNameValidator(typeIndex, propIndex, value)
                                                    }
                                                  ]}
                                                  {...propRestField}
                                                >
                                                  <KuikaInput
                                                    placeholder="Property name"
                                                    onKeyDown={(e) => {
                                                      if (!e.key.match(/^[a-zA-Z0-9_]*$/)) {
                                                        e.preventDefault();
                                                      }
                                                    }}
                                                  />
                                                </Form.Item>
                                              </div>

                                              <div className="kuika-ai-agent-custom-types-modal__column-type">
                                                <Form.Item
                                                  name={[propIndex, "datatype"]}
                                                  rules={[{ required: true, message: "Required" }]}
                                                  {...propRestField}
                                                >
                                                  <KuikaSelectbox options={TYPE_OPTIONS} placeholder="Type" />
                                                </Form.Item>
                                              </div>

                                              <div className="kuika-ai-agent-custom-types-modal__column-description">
                                                <Form.Item name={[propIndex, "description"]} {...propRestField}>
                                                  <KuikaInput placeholder="Description (optional)" />
                                                </Form.Item>
                                              </div>

                                              <div className="kuika-ai-agent-custom-types-modal__column-actions">
                                                <KuikaButton
                                                  type="tertiary"
                                                  color="neutral"
                                                  isIcon
                                                  onClick={() => removeProp(propIndex)}
                                                >
                                                  <KuikaSvg type="common" name="trash" color="var(--red-5)" />
                                                </KuikaButton>
                                              </div>
                                            </div>
                                          ))}

                                          <div className="kuika-ai-agent-custom-types-modal__add-property">
                                            <KuikaButton
                                              htmlType="button"
                                              size="sm"
                                              type="tertiary"
                                              onClick={() => {
                                                addProp({ name: "", datatype: "str", description: "" });
                                              }}
                                            >
                                              Add property
                                            </KuikaButton>
                                          </div>
                                        </>
                                      )}
                                    </Form.List>
                                  </div>
                                )
                              }
                            ]}
                          />
                        </div>
                      ))
                    ) : (
                      <KuikaEmptyState
                        svgName="layers"
                        svgType="common"
                        name="custom data types"
                        pastTense="defined"
                        description="Add a new type to get started."
                      />
                    )}

                    <div className="kuika-ai-agent-custom-types-modal__add-type">
                      <KuikaButton
                        htmlType="button"
                        size="sm"
                        type="tertiary"
                        onClick={() => {
                          addType({ type_name: "", properties: [] });
                          // Expand the newly added type
                          this.setState((prevState) => ({
                            expandedKeys: [...prevState.expandedKeys, `customType-${typeFields.length}`]
                          }));
                        }}
                      >
                        Add custom data type
                      </KuikaButton>
                    </div>
                  </>
                )}
              </Form.List>
            </div>
          </KModal>
        </Form>
      </>
    );
  }
}
